import { ChangeEvent } from 'react';
import cn from 'classnames';
import { AddressInfo } from 'models/AddressInfo';
import { CustomerType } from 'models/CustomerType';
import { NO_ELEMENT, rm } from 'utils';
import { RenderCheckbox, RenderLabeledInput } from 'components/Shared/Form/inputs';
import connect from 'components/Shared/connect';
import { LoaderButton } from 'components/Button';
import { reloadCart } from 'components/Cart/actions';
import { postGetAddressInfo } from 'services/ApiCheckoutPageService';
import batchAction from 'store/batching';
import autoForm, {
  AutoFormOwnProps,
  AutoFormMergedProps,
  QuickStartFormProps,
  AddressFormProps
} from '../AutoForm';
import { RenderAddress } from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/AutoForm/renderAddress';
import {
  checkoutForm,
  checkoutFormName,
  getAddressInfo,
  selectDisableMessageBoxFeatureToggle
} from 'views/CheckoutPage/selector';
import { EventWithDataHandler, Field, Fields, resetSection } from 'redux-form';
import * as rules from 'views/CheckoutPage/validation';
import {
  deliveryAddressChanged,
  setAddressDetails,
  onBlurCheckout
} from 'views/CheckoutPage/actions';
import ContactInfoPolicy from 'views/CheckoutPage/PolicyInformation/themes/BGH-RD3/contactInfoPolicy';
import { LeaveMessage } from '../leaveMessage';
import commonStyles from '../styles.scss';
import { CertificateForm } from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/certificateForm';

const MIN_ID_LENGTH = 10;

interface QuickStartFormMergedprops extends QuickStartFormProps {
  isMobile: boolean;
  form: AddressInfo;
}

const QuickStartForm = connect<QuickStartFormMergedprops, QuickStartFormProps>(state => ({
  form: getAddressInfo(state)
}))(({
  onChange,
  inputHandler,
  noAddressError,
  loading,
  handleSubmit,
  submitDisabled,
  form
}: QuickStartFormMergedprops) => {
  const hasFetchedAddressWithIdentityNumber =
    !!form.identityNumber && !!form.lastName && !!form.city && !!form.street && !!form.zipCode;

  if (hasFetchedAddressWithIdentityNumber) {
    return NO_ELEMENT;
  }

  const skipValidate = !form.identityNumber || form.identityNumber.length < MIN_ID_LENGTH;

  return (
    <div className={commonStyles.quickStartForm}>
      <Field
        name="identityNumber"
        inputMode="numeric"
        pattern={rules.organizationNumberRegexString}
        className={commonStyles.identityNumber}
        validate={[rules.organizationNumber, () => noAddressError]}
        tooltip={rm('checkout_identityheader_help_company')}
        component={RenderLabeledInput}
        props={{
          customLabel: rm('checkout_identityheader_company'),
          hideError: skipValidate
        }}
        onChange={onChange}
        onKeyPress={inputHandler}
      />
      <LoaderButton
        appearance="quaternary"
        loading={loading}
        className={commonStyles.getAddressButton}
        disabled={submitDisabled}
        onClick={handleSubmit}
        type="button"
      >
        {rm('checkout_identity_button')}
      </LoaderButton>
    </div>
  );
});

interface MergedProps extends AddressFormProps {
  deliveryAddressChanged: EventWithDataHandler<ChangeEvent>;
  onBlur: EventWithDataHandler<ChangeEvent>;
  shouldDisableMessageBoxFeatureToggle: boolean;
}

const AddressForm = connect<MergedProps>(
  state => ({
    shouldDisableMessageBoxFeatureToggle: selectDisableMessageBoxFeatureToggle(state)
  }),
  dispatch => ({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onBlur: (_e: ChangeEvent, n: any, _p: any, name: string) => dispatch(onBlurCheckout(name, n)),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deliveryAddressChanged: (_e: ChangeEvent, n: any, _p: any, name: string) =>
      dispatch(deliveryAddressChanged(n, name))
  })
)(
  ({
    deliveryAddressChanged,
    onBlur,
    resetForm,
    shouldDisableMessageBoxFeatureToggle
  }: MergedProps) => (
    <>
      <div className={commonStyles.left}>
        <Fields
          names={['identityNumber', 'firstName', 'lastName', 'street', 'city', 'zipCode', 'county']}
          label={rm('checkout_address')}
          component={RenderAddress(resetForm)}
        />
      </div>
      <ContactInfoPolicy desktop marginBottom className={commonStyles.right} />
      <Field
        name="hasDeliveryAddress"
        onChange={deliveryAddressChanged}
        component={RenderCheckbox}
        label={rm('checkout_delivery_alternative_label')}
        className={cn(commonStyles.checkboxWrapper, commonStyles.altDelivery)}
      />
      <Field
        name="reference"
        className={cn(commonStyles.input, commonStyles.left)}
        component={RenderLabeledInput}
        props={{ customLabel: rm('checkout_reference') }}
        onBlur={onBlur}
      />
      <Field
        name="littra"
        className={cn(commonStyles.input, commonStyles.right)}
        component={RenderLabeledInput}
        props={{ customLabel: rm('checkout_littra') }}
        onBlur={onBlur}
      />
      <Field
        name="phoneNumber"
        type="tel"
        tooltip={rm('checkout_cellphone_help')}
        validate={rules.phone}
        className={cn(commonStyles.input, commonStyles.left)}
        component={RenderLabeledInput}
        props={{ customLabel: rm('checkout_cellphone') }}
        onBlur={onBlur}
        inputMode="tel"
      />
      <Field
        name="email"
        type="email"
        validate={rules.email}
        className={cn(commonStyles.input, commonStyles.right)}
        component={RenderLabeledInput}
        props={{ customLabel: rm('checkout_email_rd3') }}
        onBlur={onBlur}
      />
      {shouldDisableMessageBoxFeatureToggle ? (
        NO_ELEMENT
      ) : (
        <LeaveMessage className={commonStyles.message} onBlur={onBlur} />
      )}
      <CertificateForm />
      <ContactInfoPolicy mobile />
    </>
  )
);

interface StateProps {
  address: AddressInfo;
}

export default connect<AutoFormMergedProps, AutoFormOwnProps, StateProps>(
  state => ({
    address: checkoutForm(state, 'address') as AddressInfo
  }),
  dispatch => ({
    setAddressDetails: (args: AddressInfo) =>
      dispatch(batchAction(setAddressDetails(args), reloadCart())),
    reset: () => dispatch(resetSection(checkoutFormName, 'address.deliveryAddress'))
  }),
  ({ address, ...state }, { loadDetails, setAddressDetails, ...dispatch }, ownProps) => ({
    ...state,
    ...dispatch,
    ...ownProps,
    isValid: address && rules.isValidOrganizationNumber(address.identityNumber),
    showAddressForm: Boolean(address && address.street),
    hasDeliveryAddress: address && address.hasDeliveryAddress,
    loadDetails: () =>
      postGetAddressInfo({
        customerType: CustomerType.Company,
        number: address.identityNumber
      }),
    setAddressDetails: (args: AddressInfo) =>
      setAddressDetails({
        ...args,
        identityNumber: address && address.identityNumber
      })
  })
)(autoForm(AddressForm, QuickStartForm));
